import React, { useCallback, useEffect, useState } from 'react';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { useUserInfo } from '@/shared/hooks/useUserInfo';
import { standardAreaApi } from '../api/StandardAreaApi';
import { SaveStandardAreaRequest, StandardAreaDto } from '../types/standardArea.types';
import StandardAreaModal from './StandardAreaModal';
import ConfirmModal from '@/shared/components/feedback/ConfirmModal';
import Pagination from "@/shared/components/dataDisplay/Pagination";
import '../styles/StandardArea.css';

const MAX_VISIBLE_SUPPLIERS = 3;
const PAGE_SIZE = 15;

const StandardAreaManagementPage: React.FC = () => {
    const { showToast } = useToast();
    const { getUserInfo } = useUserInfo();

    const [areas, setAreas] = useState<StandardAreaDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [nameFilter, setNameFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    const [modalOpen, setModalOpen] = useState(false);
    const [editTarget, setEditTarget] = useState<StandardAreaDto | null>(null);

    const [deleteTarget, setDeleteTarget] = useState<StandardAreaDto | null>(null);
    const [autoRegisterConfirm, setAutoRegisterConfirm] = useState(false);

    // ===== 데이터 조회 =====

    const fetchAreas = useCallback(async () => {
        setLoading(true);
        try {
            const res = await standardAreaApi.getStandardAreas();
            setAreas(res.areas ?? []);
            setCurrentPage(1);
        } catch {
            showToast('표준권역 목록 조회에 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    useEffect(() => {
        fetchAreas();
    }, [fetchAreas]);

    // ===== 등록/수정 =====

    const handleSave = async (req: SaveStandardAreaRequest) => {
        const userInfo = getUserInfo();
        if (!userInfo) return;
        try {
            await standardAreaApi.saveStandardArea({ ...req, userId: userInfo.userId });
            showToast(req.seqNo ? '수정되었습니다.' : '등록되었습니다.', { type: ToastType.SUCCESS });
            setModalOpen(false);
            fetchAreas();
        } catch (error: any) {
            const msg =
                error?.originalError?.response?.data?.message ||
                error?.message ||
                '저장에 실패했습니다.';
            showToast(msg, { type: ToastType.ERROR });
        }
    };

    // ===== 삭제 =====

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        try {
            await standardAreaApi.deleteStandardArea(deleteTarget.seqNo);
            showToast('삭제되었습니다.', { type: ToastType.SUCCESS });
            setDeleteTarget(null);
            fetchAreas();
        } catch {
            showToast('삭제에 실패했습니다.', { type: ToastType.ERROR });
        }
    };

    // ===== 기본설정값 등록 =====

    const handleAutoRegister = async () => {
        const userInfo = getUserInfo();
        if (!userInfo) return;
        setAutoRegisterConfirm(false);
        try {
            const result = await standardAreaApi.autoRegister(userInfo.userId);
            showToast(
                `자동 등록 완료: 권역 ${result.newAreaCount}개, 매핑 ${result.newMappingCount}개 신규 추가`,
                { type: ToastType.SUCCESS }
            );
            fetchAreas();
        } catch {
            showToast('자동 등록에 실패했습니다.', { type: ToastType.ERROR });
        }
    };

    const filteredAreas = nameFilter.trim()
        ? areas.filter(a => a.areaName.includes(nameFilter.trim()))
        : areas;
    const totalPages = Math.ceil(filteredAreas.length / PAGE_SIZE);
    const pagedAreas = filteredAreas.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    return (
        <div style={{ padding: '24px' }}>
            {/* 헤더 */}
            <div className="sa-page-header">
                <h1 className="sa-page-title">표준권역 관리</h1>
                <div className="sa-header-actions">
                    <input
                        type="text"
                        value={nameFilter}
                        onChange={e => { setNameFilter(e.target.value); setCurrentPage(1); }}
                        placeholder="표준권역명 검색"
                        style={{
                            padding: '7px 10px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '0.875rem',
                            width: '160px',
                        }}
                    />
                    <button className="sa-btn-auto" onClick={() => setAutoRegisterConfirm(true)}>
                        기본설정값 등록
                    </button>
                    <button
                        className="sa-btn-add"
                        onClick={() => { setEditTarget(null); setModalOpen(true); }}
                    >
                        + 권역 추가
                    </button>
                </div>
            </div>

            {/* 테이블 */}
            <div className="sa-table-wrap">
                <table className="sa-table">
                    <thead>
                    <tr>
                        <th style={{ width: 60 }}>No.</th>
                        <th className="text-left" style={{ width: 180 }}>표준권역명</th>
                        <th className="text-left">소속 협력사</th>
                        <th style={{ width: 120 }}>등록일</th>
                        <th style={{ width: 140 }}>관리</th>
                    </tr>
                    </thead>
                    <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan={5} className="sa-empty">조회 중...</td>
                        </tr>
                    ) : filteredAreas.length === 0 ? (
                        <tr>
                            <td colSpan={5} className="sa-empty">
                                {nameFilter.trim() ? '검색 결과가 없습니다.' : '등록된 표준권역이 없습니다.'}
                            </td>
                        </tr>
                    ) : (
                        pagedAreas.map((area, idx) => (
                            <tr key={area.seqNo}>
                                <td>{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                                <td className="text-left" style={{ fontWeight: 500 }}>
                                    {area.areaName}
                                </td>
                                <td className="text-left">
                                    {area.suppliers.length === 0 ? (
                                        <span style={{ color: '#9ca3af', fontSize: '0.8125rem' }}>없음</span>
                                    ) : (
                                        <div className="sa-supplier-list">
                                            {area.suppliers
                                                .slice(0, MAX_VISIBLE_SUPPLIERS)
                                                .map((s) => (
                                                    <span key={s.suppId} className="sa-supplier-tag">
                                                            {s.suppName || s.suppId}
                                                        </span>
                                                ))}
                                            {area.suppliers.length > MAX_VISIBLE_SUPPLIERS && (
                                                <span className="sa-supplier-more">
                                                        +{area.suppliers.length - MAX_VISIBLE_SUPPLIERS}개 더
                                                    </span>
                                            )}
                                        </div>
                                    )}
                                </td>
                                <td>{area.insDate ?? '-'}</td>
                                <td>
                                    <button
                                        className="btn-edit"
                                        onClick={() => { setEditTarget(area); setModalOpen(true); }}
                                    >
                                        수정
                                    </button>
                                    <button
                                        className="btn-delete"
                                        onClick={() => setDeleteTarget(area)}
                                    >
                                        삭제
                                    </button>
                                </td>
                            </tr>
                        ))
                    )}
                    </tbody>
                </table>
            </div>
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
            />

            {/* 등록/수정 모달 */}
            <StandardAreaModal
                isOpen={modalOpen}
                editTarget={editTarget}
                onSave={handleSave}
                onClose={() => setModalOpen(false)}
            />

            {/* 삭제 확인 */}
            <ConfirmModal
                isOpen={!!deleteTarget}
                title="표준권역 삭제"
                message={deleteTarget ? `"${deleteTarget.areaName}" 권역을 삭제하시겠습니까?\n소속 협력사 매핑도 함께 삭제됩니다.` : ''}
                onConfirm={() => { void handleDeleteConfirm(); }}
                onCancel={() => setDeleteTarget(null)}
            />

            {/* 기본설정값 등록 확인 */}
            <ConfirmModal
                isOpen={autoRegisterConfirm}
                title="기본설정값 등록"
                message={`협력사명에서 "부릉" 이전 문자열을 추출하여 표준권역을 자동 등록합니다.\n이미 등록된 권역/매핑은 건너뜁니다. 진행하시겠습니까?`}
                onConfirm={() => { void handleAutoRegister(); }}
                onCancel={() => setAutoRegisterConfirm(false)}
            />
        </div>
    );
};

export default StandardAreaManagementPage;
